import { type FC, useEffect, useRef, useState } from 'react'
import { ChevronDownIcon } from '@/components/ui/icons'
import { cn } from '@/helpers/utils'
import { useTranslation } from '@/translations/i18n'

/** Keep in sync with the `line-clamp-3` class below. */
const VISIBLE_LINES = 3

interface EscalationReasonCellProps {
	note: string
}

/**
 * The escalation reason is surfaced in the queue so an Ops user reads it without
 * opening a second view: it wraps over three lines, and a longer note expands
 * the row in place rather than hiding the rest behind a hover tooltip nobody
 * discovers. The `py-2` applies in both states so toggling grows the row
 * downwards without shifting the text.
 */
export const EscalationReasonCell: FC<EscalationReasonCellProps> = ({
	note
}) => {
	const { t } = useTranslation()
	const textRef = useRef<HTMLParagraphElement>(null)
	const [isClamped, setIsClamped] = useState(false)
	const [isExpanded, setIsExpanded] = useState(false)

	useEffect(() => {
		const element = textRef.current
		if (!element) {
			return
		}

		// A clamped element reports scrollHeight === clientHeight, so measure the
		// text's natural height instead: drop the -webkit-box display for the
		// measurement, read the full height, then put it back.
		const measure = () => {
			const { width } = element.getBoundingClientRect()
			if (width === 0) {
				return
			}

			const lineHeight =
				Number.parseFloat(getComputedStyle(element).lineHeight) || 20

			const display = element.style.display
			element.style.display = 'block'
			const fullHeight = element.scrollHeight
			element.style.display = display

			setIsClamped(fullHeight > lineHeight * VISIBLE_LINES + 1)
		}

		const observer = new ResizeObserver(measure)
		observer.observe(element)
		return () => observer.disconnect()
	}, [])

	return (
		<div
			className={cn(
				'flex flex-col items-start gap-1 [tr:has(&)_td]:py-2',
				// Table cells are a fixed `h-18` and `truncate`, so an expanded
				// note has to lift both from every cell in its row — otherwise
				// the text is clipped and the neighbouring cells stay short.
				// h-0 rather than h-auto: the table keeps a spacer row that
				// stretches it to the wrapper, and `auto` lets the browser hand
				// that leftover height to this row, blowing it up when it is the
				// only one. A zero base makes the row take its content's height.
				isExpanded &&
					'[tr:has(&)_td]:h-0 [tr:has(&)_td]:overflow-visible [tr:has(&)_td]:whitespace-normal'
			)}
		>
			<p
				className={cn(
					'wrap-break-word whitespace-normal text-left text-neutral-700 text-sm leading-5',
					!isExpanded && 'line-clamp-3'
				)}
				ref={textRef}
			>
				{note}
			</p>

			{isClamped && (
				<button
					className='flex shrink-0 cursor-pointer items-center gap-1 font-medium text-neutral-900 text-sm underline'
					onClick={() => setIsExpanded((expanded) => !expanded)}
					type='button'
				>
					{t(
						isExpanded
							? 'luxe_submissions:show_less'
							: 'luxe_submissions:show_more'
					)}
					<ChevronDownIcon
						className={cn(
							'transition-transform duration-150',
							isExpanded && 'rotate-180'
						)}
						size={16}
					/>
				</button>
			)}
		</div>
	)
}
